import React, { useState, useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { QRCodeCanvas } from "qrcode.react";
import toast from "react-hot-toast";

import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const BarcodeRenderer = ({ value }) => {

    const barcodeRef = useRef(null);

    useEffect(() => {

        if (barcodeRef.current && value) {

            JsBarcode(barcodeRef.current, value, {
                format: "CODE128",
                lineColor: "#111827",
                width: 1.8,
                height: 70,
                displayValue: true,
                fontSize: 16,
                margin: 10,
                background: "#ffffff"
            });

        }

    }, [value]);

    return <svg ref={barcodeRef}></svg>;

};

const BarcodeDisplay = ({ asset }) => {

    const [isPrinting, setIsPrinting] = useState(false);

    const handleCopy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch (error) {
            console.error(error);
            toast.error(`Failed to copy ${label}`);
        }
    };

    const handlePrint = () => {

        setIsPrinting(true);

        setTimeout(() => {

            window.print();

            setIsPrinting(false);

        }, 300);

    };

    return (

        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">

            <div className="flex items-center justify-between mb-4">

                <div className="flex items-center gap-2">

                    <Icon
                        name="QrCode"
                        size={20}
                        className="text-primary"
                    />

                    <h3 className="text-lg font-semibold">
                        Barcode & QR Code
                    </h3>

                </div>

            </div>

            {/* Barcode */}

            <div className="bg-white rounded-xl border border-border p-5">

                <div className="flex justify-center">

                    <BarcodeRenderer
                        value={asset?.barcode || asset?.asset_id}
                    />

                </div>

                <p className="text-center text-sm text-muted-foreground mt-3">

                    Linear Barcode (Code 128)

                </p>

            </div>

            {/* QR Code */}

            <div className="bg-white rounded-xl border border-border p-6 mt-6">

                <div className="flex justify-center">

                    <QRCodeCanvas
                        value={JSON.stringify({

                            asset_id: asset?.asset_id,

                            barcode: asset?.barcode,

                            serial_number: asset?.serial_number,

                            type: asset?.category?.name,

                            owner: asset?.owner?.name,

                            department: asset?.department,

                            location: asset?.location,

                            room: asset?.room,

                            brand: asset?.brand,

                            model: asset?.model,

                            status: asset?.status

                        })}
                        size={150}
                        level="H"
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#111827"
                    />

                </div>

                <p className="text-center text-sm text-muted-foreground mt-3">

                    QR Code (Asset Details)

                </p>

            </div>

            {/* Asset Information */}

            <div className="bg-muted rounded-lg p-4 mt-6">

                <div className="grid grid-cols-2 gap-4">

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Asset ID
                        </p>

                        <p className="font-mono text-sm">
                            {asset?.asset_id}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Barcode
                        </p>

                        <p className="font-mono text-sm">
                            {asset?.barcode}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Serial Number
                        </p>

                        <p className="font-mono text-sm">
                            {asset?.serial_number || "N/A"}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Asset Type
                        </p>

                        <p className="text-sm">
                            {asset?.category?.name || "N/A"}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Owner
                        </p>

                        <p className="text-sm">
                            {asset?.owner?.name || "N/A"}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Status
                        </p>

                        <p className="text-sm capitalize">
                            {asset?.status}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Location
                        </p>

                        <p className="text-sm">
                            {asset?.location || "N/A"}
                        </p>

                    </div>

                    <div>

                        <p className="text-xs text-muted-foreground">
                            Room
                        </p>

                        <p className="text-sm">
                            {asset?.room || "N/A"}
                        </p>

                    </div>

                </div>

            </div>

            {/* Buttons */}

            <div className="grid grid-cols-2 gap-3 mt-5">

                <Button
                    variant="outline"
                    size="sm"
                    iconName="Copy"
                    iconPosition="left"
                    onClick={() =>
                        handleCopy(
                            asset?.barcode || asset?.asset_id,
                            "Barcode"
                        )
                    }
                >

                    Copy Code

                </Button>

                <Button
                    variant="outline"
                    size="sm"
                    iconName="Printer"
                    iconPosition="left"
                    loading={isPrinting}
                    onClick={handlePrint}
                >

                    Print

                </Button>

            </div>

            {/* Instructions */}

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mt-6">

                <div className="flex items-start gap-3">

                    <Icon
                        name="Info"
                        size={16}
                        className="text-accent mt-1"
                    />

                    <div>

                        <p className="font-medium">

                            Scanning Instructions

                        </p>

                        <ul className="text-xs text-muted-foreground mt-2 space-y-1">

                            <li>• Scan barcode for quick identification.</li>

                            <li>• QR Code contains complete asset information.</li>

                            <li>• Print labels using a label printer.</li>

                            <li>• Attach labels to visible asset surfaces.</li>

                        </ul>

                    </div>

                </div>

            </div>

        </div>

    );

};

export default BarcodeDisplay;